/**
 * Fractional index keys for ordering sibling nodes, a direct port of the
 * backend's `sort_key.py`.
 *
 * The optimistic move needs to place the dragged nodes *between* their new
 * neighbours using keys in the same base-62 space the server uses, and touch
 * nothing else. Reindexing the whole column with throwaway keys instead would
 * desync from the server: the move endpoint rewrites only the moved nodes, so
 * when its response merges back the untouched siblings would still be carrying
 * the throwaway keys and the moved node would jump out of place until a reload.
 *
 * BigInt mirrors the backend's arbitrary-precision integers so long keys stay
 * exact rather than drifting once the numerator passes 2**53.
 */

const DIGITS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
const BASE = BigInt(DIGITS.length);
const ZERO = DIGITS[0];

const toInt = (key: string, length: number): bigint => {
  let value = 0n;
  const padded = key.padEnd(length, ZERO);
  for (const char of padded)
    value = value * BASE + BigInt(DIGITS.indexOf(char));
  return value;
};

const toKey = (value: bigint, length: number): string => {
  const digits: string[] = [];
  let remaining = value;
  for (let i = 0; i < length; i += 1) {
    const remainder = remaining % BASE;
    remaining /= BASE;
    digits.push(DIGITS[Number(remainder)]);
  }
  return digits.reverse().join("");
};

/** Drop trailing zero digits so the key stays canonical (see the module docs). */
const stripTrailingZeros = (key: string): string => key.replace(/0+$/, "");

/**
 * `count` ascending keys strictly between the two bounds, spread evenly across
 * the gap in one pass. `null` means unbounded on that side.
 */
export const keysBetween = (
  prev: string | null,
  next: string | null,
  count: number
): string[] => {
  if (count <= 0) return [];

  const lower = prev ?? "";
  let length = Math.max(lower.length, (next ?? "").length);

  for (;;) {
    length += 1;
    const low = toInt(lower, length);
    const high = next === null ? BASE ** BigInt(length) : toInt(next, length);
    const span = high - low;

    // Only worth splitting once the gap can hold every key plus a margin.
    if (span >= BigInt(count + 1)) {
      const keys: string[] = [];
      for (let index = 1; index <= count; index += 1) {
        const raw = low + (span * BigInt(index)) / BigInt(count + 1);
        const candidate = stripTrailingZeros(toKey(raw, length));
        if (!candidate || candidate <= lower) break;
        if (keys.length && candidate <= keys[keys.length - 1]) break;
        if (next !== null && candidate >= next) break;
        keys.push(candidate);
      }
      if (keys.length === count) return keys;
    }
  }
};
